<script lang="ts">
	import { Text, Stack } from '@Shelly/shelly-design-system';
	import type { FlowNode } from '../../types/flow-types';
	import { flowEditorState } from '../../../../stores/flow_editor';

	export let node: FlowNode;

	type ScheduleInterval = {
		from: string;
		to: string;
		days: number[];
	};

	const dayOptions = [
		{ id: 1, label: 'Mon' },
		{ id: 2, label: 'Tue' },
		{ id: 3, label: 'Wed' },
		{ id: 4, label: 'Thu' },
		{ id: 5, label: 'Fri' },
		{ id: 6, label: 'Sat' },
		{ id: 0, label: 'Sun' },
	];

	function normalizeInterval(raw: any): ScheduleInterval {
		return {
			from: typeof raw?.from === 'string' && raw.from ? raw.from : '08:00',
			to: typeof raw?.to === 'string' && raw.to ? raw.to : '18:00',
			days:
				Array.isArray(raw?.days) && raw.days.length > 0
					? raw.days.map((d: any) => Number(d)).filter((d: number) => d >= 0 && d <= 6)
					: [1, 2, 3, 4, 5],
		};
	}

	$: intervals = (
		Array.isArray((node.data as any).activeTimeIntervals)
			? ((node.data as any).activeTimeIntervals as any[])
			: []
	).map((it) => normalizeInterval(it));

	function commit(nextIntervals: ScheduleInterval[]) {
		flowEditorState.updateNode(node.id, {
			data: {
				...node.data,
				activeTimeIntervals: nextIntervals.map((it) => ({
					from: it.from,
					to: it.to,
					days: [...it.days],
				})),
			} as any,
		});
	}

	function addInterval() {
		commit([...intervals, { from: '08:00', to: '18:00', days: [1, 2, 3, 4, 5] }]);
	}

	function removeInterval(index: number) {
		commit(intervals.filter((_, i) => i !== index));
	}

	function updateTime(index: number, key: 'from' | 'to', value: string) {
		const next = intervals.map((it, i) => (i === index ? { ...it, [key]: value } : it));
		commit(next);
	}

	function handleFromChange(index: number, e: Event) {
		updateTime(index, 'from', (e.target as HTMLInputElement).value);
	}

	function handleToChange(index: number, e: Event) {
		updateTime(index, 'to', (e.target as HTMLInputElement).value);
	}

	function toggleDay(index: number, day: number) {
		const next = intervals.map((it, i) => {
			if (i !== index) return it;
			const has = it.days.includes(day);
			const days = has ? it.days.filter((d) => d !== day) : [...it.days, day];
			return { ...it, days: days.sort((a, b) => a - b) };
		});
		commit(next);
	}
</script>

<div class="schedule-inspector">
	<Stack direction="row" gap="m">
		<Text variant="h3">Schedule</Text>
		<Text variant="body2">Define when this flow is allowed to execute.</Text>

		{#if intervals.length === 0}
			<div class="empty">No intervals configured.</div>
		{/if}

		{#each intervals as interval, index}
			<div class="interval-card">
				<div class="interval-header">
					<strong>Interval {index + 1}</strong>
					<button
						class="remove-btn"
						on:click={() => removeInterval(index)}
						aria-label="Remove interval"
					>
						<i class="fa fa-trash" />
					</button>
				</div>
				<div class="time-row">
					<label for={'schedule-from-' + index}>From</label>
					<input
						id={'schedule-from-' + index}
						type="time"
						value={interval.from}
						on:change={(e) => handleFromChange(index, e)}
					/>
					<label for={'schedule-to-' + index}>To</label>
					<input
						id={'schedule-to-' + index}
						type="time"
						value={interval.to}
						on:change={(e) => handleToChange(index, e)}
					/>
				</div>
				<div class="days-row">
					{#each dayOptions as day}
						<button
							class="day-chip"
							class:active={interval.days.includes(day.id)}
							on:click={() => toggleDay(index, day.id)}
							type="button"
						>
							{day.label}
						</button>
					{/each}
				</div>
			</div>
		{/each}

		<button class="add-btn" on:click={addInterval} type="button">
			<i class="fa fa-plus" />
			Add interval
		</button>
	</Stack>
</div>

<style>
	.schedule-inspector {
		padding: 16px;
	}

	.empty {
		color: #9ca3af;
		font-size: 13px;
	}

	.interval-card {
		background: rgba(255, 255, 255, 0.04);
		border: 1px solid rgba(255, 255, 255, 0.15);
		border-radius: 10px;
		padding: 10px;
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.interval-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		color: #fff;
	}

	.remove-btn {
		width: 28px;
		height: 28px;
		border-radius: 6px;
		border: 1px solid rgba(239, 68, 68, 0.5);
		background: rgba(239, 68, 68, 0.15);
		color: #fecaca;
		cursor: pointer;
	}

	.time-row {
		display: grid;
		grid-template-columns: 40px 1fr 22px 1fr;
		gap: 8px;
		align-items: center;
	}

	.time-row label {
		color: #9ca3af;
		font-size: 12px;
	}

	.time-row input {
		height: 32px;
		border-radius: 8px;
		border: 1px solid rgba(255, 255, 255, 0.2);
		background: rgba(255, 255, 255, 0.08);
		color: #fff;
		padding: 0 8px;
	}

	.days-row {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}

	.day-chip {
		height: 28px;
		padding: 0 8px;
		border-radius: 999px;
		border: 1px solid rgba(255, 255, 255, 0.22);
		background: transparent;
		color: #d1d5db;
		font-size: 12px;
		cursor: pointer;
	}

	.day-chip.active {
		border-color: rgba(99, 102, 241, 0.7);
		background: rgba(99, 102, 241, 0.25);
		color: #e0e7ff;
	}

	.add-btn {
		height: 34px;
		align-self: flex-start;
		padding: 0 12px;
		border-radius: 8px;
		border: 1px solid rgba(99, 102, 241, 0.5);
		background: rgba(99, 102, 241, 0.2);
		color: #fff;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		gap: 8px;
	}
</style>
